import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Award, Users, Clock, Shield } from 'lucide-react';

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const stats = [
    { icon: Award, value: '15+', label: 'Years Experience' },
    { icon: Users, value: '500+', label: 'Happy Clients' },
    { icon: Clock, value: '100%', label: 'On-Time Delivery' },
    { icon: Shield, value: '5 Year', label: 'Warranty' },
  ];

  return (
    <section
      id="about"
      data-testid="about-section"
      ref={ref}
      className="section-padding bg-white"
    >
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1682662046610-fbdb3db4bd74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBsaXZpbmclMjByb29tJTIwaG9tZSUyMHJlbm92YXRpb24lMjBicmlnaHQlMjBlbGVnYW50fGVufDB8fHx8MTc3Mjc4MjgwN3ww&ixlib=rb-4.1.0&q=85"
                alt="Luxury home renovation"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Accent Box */}
            <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#D4AF37] hidden md:block" />
            <div className="absolute -bottom-3 -right-3 w-48 h-48 border-2 border-[#0A0A0A] hidden md:block" />
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="text-[#D4AF37] text-sm font-medium tracking-[0.2em] uppercase mb-4">
              About Lux Renovations
            </p>
            <h2
              data-testid="about-title"
              className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#0A0A0A] font-bold mb-6 leading-tight"
            >
              Your Trusted Local
              <br />
              Renovation Experts
            </h2>
            <div className="w-16 h-0.5 bg-[#D4AF37] mb-8" />

            <p className="text-[#71717A] text-base md:text-lg leading-relaxed mb-6">
              Lux Renovations is a trusted local renovation contractor led by{' '}
              <span className="text-[#0A0A0A] font-medium">Cristian</span>, known
              for professionalism, clear communication, and high-quality
              craftsmanship. We take pride in transforming homes throughout
              Gaithersburg and the surrounding Maryland areas.
            </p>
            <p className="text-[#71717A] text-base md:text-lg leading-relaxed mb-8">
              We work closely with homeowners from the initial consultation to the
              final touches, ensuring every project meets—and exceeds—expectations.
              Our attention to detail and commitment to excellence sets us apart.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-[#E4E4E7]">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  className="text-center"
                >
                  <stat.icon className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                  <p className="font-serif text-2xl font-bold text-[#0A0A0A]">
                    {stat.value}
                  </p>
                  <p className="text-xs text-[#71717A] tracking-wide">
                    {stat.label}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
